import { createSelector } from "reselect";
import {
    SET_PIPE_RISER,
    SET_PIPE_INVERT,
    SET_PIPE_CLEANOUT,
    SET_PIPE_INSERTA_TEE,
    PIPE_SET_PITCH,
    PIPE_SET_MANIFOLD_T,
    PIPE_SET_MANIFOLD_CORNER,
    PIPE_SET_BED_DIMENSIONS,
    PIPE_TYPE_SOIL_TIGHT_PERFORATED_ID,
    PIPE_SET_BED_POSITION,
    PIPE_SET_BED_TRANSFORMATION,
    PIPE_SET_BED_CENTRALICED_COORDINATES,
    CONTEXT_SELECTED_PIPE_ELEMENT,
    PIPE_FLOW_BASIN_POSITION, IS_DIRTY,
} from "../lib/types";
import { InchUtility, pipeFittings, PipeFlowDirection } from "../lib/pipe-dataset";
import { getUnscaledFeet } from "../lib/calculations";
import pipeSelectors from "../lib/pipe-selectors";
import { setPipeParameterValue } from "./pipe-calculations.actions";

export const pipeBedSelectElement = ( id ) => dispatch => {
    dispatch({
        type: CONTEXT_SELECTED_PIPE_ELEMENT,
        payload: id,
    });
};
export const pipeBedSetCentralizedCoordinates = coordinates => dispatch => {
    dispatch({
        type: PIPE_SET_BED_CENTRALICED_COORDINATES,
        payload: coordinates,
    });
};

export const setPipeTransformation = ( rotation, x, y, length, width ) => ( dispatch, getState ) => {
    const {
        pipeBed,
    } = getState();

    const {
        height: actualLength,
        width: actualWidth,
    } = pipeBed.stoneBedDimensions;

    dispatch({
        type: PIPE_SET_BED_TRANSFORMATION,
        payload: {
            rotation,
            x,
            y,
        },
    });
    let lengthFt = length !== null ? getUnscaledFeet(length) : actualLength.ft;
    let widthFt = width !== null ? getUnscaledFeet(width) : actualWidth.ft;

    widthFt <= 2
        ? setPipeDesign(2, lengthFt)(dispatch, getState)
        : setPipeDesign(widthFt, lengthFt)(dispatch, getState);

    dispatch({
        type: IS_DIRTY,
        payload: true,
    });
};

export const rotatePipeBed = ( rotation, x, y ) => dispatch => {
    dispatch({
        type: PIPE_SET_BED_TRANSFORMATION,
        payload: {
            rotation,
            x,
            y,
        },
    });
    dispatch({
        type: IS_DIRTY,
        payload: true,
    });
};

export const setPipeRiser = ( data ) => dispatch => {
    dispatch({
        type: SET_PIPE_RISER,
        payload: data,
    });
};

export const setPipeInvert = ( data ) => dispatch => {
    dispatch({
        type: SET_PIPE_INVERT,
        payload: data,
    });
};
export const setPipeCleanout = ( data ) => dispatch => {
    dispatch({
        type: SET_PIPE_CLEANOUT,
        payload: data,
    });
};

export const setPipeInsertaTee = ( data ) => dispatch => {
    dispatch({
        type: SET_PIPE_INSERTA_TEE,
        payload: data,
    });
};

export const setPitch = ( pitch ) => dispatch => {
    dispatch({
        type: PIPE_SET_PITCH,
        payload: pitch,
    });
};

export const setManifoldT = ( pipeManifoldT ) => dispatch => {
    dispatch({
        type: PIPE_SET_MANIFOLD_T,
        payload: pipeManifoldT,
    });
};

export const setManifoldCorner = ( pipeManifoldCorner ) => dispatch => {
    dispatch({
        type: PIPE_SET_MANIFOLD_CORNER,
        payload: pipeManifoldCorner,
    });
};

export const setPipeBedPosition = ( position ) => dispatch => {
    dispatch({
        type: PIPE_SET_BED_POSITION,
        payload: position,
    });
    dispatch({
        type: IS_DIRTY,
        payload: true,
    });
};

export const setPipeBedDimensions = ( dimensions ) => ( dispatch, getState ) => {
    const { pipeGlobals, pipeBed } = getState();

    dispatch({
        type: PIPE_SET_BED_DIMENSIONS,
        payload: {
            state: getState(),
            ...dimensions,
            pipeTypeName: pipeGlobals.pipeFitting._name,
            pipeSizeNominalDiameter: pipeGlobals.pipeNominalDiameter._pipeND._inches,
            pipeBed,
        },
    });
    dispatch({
        type: IS_DIRTY,
        payload: true,
    });
};

export const setPipeFlowDirection = position => ( dispatch, getState ) => {
    let flowDirection;

    switch (position) {
        case PIPE_FLOW_BASIN_POSITION.TOP_RIGHT:
            flowDirection = PipeFlowDirection.BottomLeftToTopRight;
            break;
        case PIPE_FLOW_BASIN_POSITION.TOP_LEFT:
            flowDirection = PipeFlowDirection.BottomRightToTopLeft;
            break;
        case PIPE_FLOW_BASIN_POSITION.BOTTOM_RIGHT:
            flowDirection = PipeFlowDirection.TopLeftToBottomRight;
            break;
        case PIPE_FLOW_BASIN_POSITION.BOTTOM_LEFT:
            flowDirection = PipeFlowDirection.TopRightToBottomLeft;
            break;
        default:
            return;
    }

    setPipeParameterValue("pipeFlowDirection", flowDirection)(dispatch, getState);
};

export const generatePipeDesign = () => ( dispatch, getState ) => {
    const design = getRectangularPipeBedDesign(getState());

    setPipeBedDimensions(design)(dispatch, getState);
};

export const setPipeDesign = ( width, height ) => ( dispatch, getState ) => {
    const design = getRectangularPipeBedDesign(getState(), width, height);

    setPipeBedDimensions(design)(dispatch, getState);
};

// TODO: Build similar approaches for irregular pipe bed designs
/**
 * Get a rectangular pipe bed design
 * @param state
 * @param widthOverride
 * @param lengthOverride
 * @returns {any | InchUtility | number | {dimensions: {width, length}, area: any, volume: any, numberOfPipeSticks, numberOfManifoldTees}}
 */
const getRectangularPipeBedDesign = ( state, widthOverride = null, lengthOverride = null ) => {
    const {
        doesPipeFlowDirectionStartOnTop,
        doesPipeFlowDirectionStartOnBottom,
        pipeLongLegLength,
        pipeShortLegLength,
        pipeRowSpacing,
        pipeNominalDiameter,
        pipeFitting,
        requiredVolume,
        availableWidth,
        availableLength,
        stoneAbove,
        stoneBelow,
        stonePorosity,
        stonePerimeterOffset,
        calculatedStoneBedMinimumWidth,
        calculatedStoneBedMinimumLength,
        _pipeTotalExcavation,
        _pipeStoneBedAmount,
        _pipeStoneBedVolume,
    } = pipeSelectors;

    const defaultResult = {
        dimensions: { width: new InchUtility(0), length: new InchUtility(0) },
        stoneBedDimensions: {
            x: new InchUtility(0),
            y: new InchUtility(0),
            width: new InchUtility(0),
            height: new InchUtility(0),
        },
        pipeBedDimensions: {
            x: new InchUtility(0),
            y: new InchUtility(0),
            width: new InchUtility(0),
            height: new InchUtility(0),
        },
        area: 0,
        volume: 0,
        rowSpacing: 0,
        numberOfPipeRows: 0,
        numberOfPipeSticks: 0,
        numberOfManifoldTees: 0,
    };

    /**
     * Get the basic pipe bed design without taking volume into account
     *
     * @type {OutputSelector<any, any, (res: any) => any> | OutputParametricSelector<any, any, any, (res: any) => any> | OutputSelector<any, InchUtility, (res1: any, res2: any) => InchUtility> | OutputParametricSelector<any, any, InchUtility, (res1: any, res2: any) => InchUtility> | OutputSelector<any, InchUtility, (res1: any, res2: any, res3: InchUtility) => InchUtility> | OutputParametricSelector<any, any, InchUtility, (res1: any, res2: any, res3: InchUtility) => InchUtility> | OutputSelector<any, InchUtility, (res1: any, res2: any, res3: InchUtility, res4: InchUtility) => InchUtility> | OutputParametricSelector<any, any, InchUtility, (res1: any, res2: any, res3: InchUtility, res4: InchUtility) => InchUtility> | OutputSelector<any, {width: InchUtility, length: InchUtility}, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility) => {width: InchUtility, length: InchUtility}> | OutputParametricSelector<any, any, {width: InchUtility, length: InchUtility}, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility) => {width: InchUtility, length: InchUtility}> | OutputSelector<any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: {width: InchUtility, length: InchUtility}) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: any, res7: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: {width: InchUtility, length: InchUtility}, res7: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: any, res7: any, res8: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: {width: InchUtility, length: InchUtility}, res7: any, res8: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: any, res7: any, res8: any, res9: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: {width: InchUtility, length: InchUtility}, res7: any, res8: any, res9: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: any, res7: any, res8: any, res9: any, res10: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: {width: InchUtility, length: InchUtility}, res7: any, res8: any, res9: any, res10: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: any, res7: any, res8: any, res9: any, res10: any, res11: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: {width: InchUtility, length: InchUtility}, res7: any, res8: any, res9: any, res10: any, res11: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: any, res7: any, res8: any, res9: any, res10: any, res11: any, res12: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: InchUtility, res4: InchUtility, res5: InchUtility, res6: {width: InchUtility, length: InchUtility}, res7: any, res8: any, res9: any, res10: any, res11: any, res12: any) => any> | OutputSelector<any, any, (res1: any, res2: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: any, res4: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: any, res4: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: any, res4: any, res5: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: any, res4: any, res5: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any, res8: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any, res8: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any, res8: any, res9: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any, res8: any, res9: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any, res8: any, res9: any, res10: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any, res8: any, res9: any, res10: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any, res8: any, res9: any, res10: any, res11: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any, res8: any, res9: any, res10: any, res11: any) => any> | OutputSelector<any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any, res8: any, res9: any, res10: any, res11: any, res12: any) => any> | OutputParametricSelector<any, any, any, (res1: any, res2: any, res3: any, res4: any, res5: any, res6: any, res7: any, res8: any, res9: any, res10: any, res11: any, res12: any) => any> | OutputSelector<any, any, (...res: any[]) => any> | OutputParametricSelector<any, any, any, (...res: any[]) => any>}
     */
    const getDesign = createSelector(
        doesPipeFlowDirectionStartOnTop,
        doesPipeFlowDirectionStartOnBottom,
        pipeLongLegLength,
        pipeShortLegLength,
        pipeRowSpacing,
        pipeNominalDiameter,
        pipeFitting,
        requiredVolume,
        availableWidth,
        availableLength,
        stoneAbove,
        stoneBelow,
        stonePorosity,
        stonePerimeterOffset,
        calculatedStoneBedMinimumWidth,
        calculatedStoneBedMinimumLength,
        ( doesPipeFlowDirectionStartOnTop, doesPipeFlowDirectionStartOnBottom, pipeLongLegLength, pipeShortLegLength, pipeRowSpacing,
          pipeNominalDiameter, pipeFitting,
          requiredVolume, availableWidth, availableLength,
          stoneAbove, stoneBelow, stonePorosity, stonePerimeterOffset,
          calculatedstoneBedMinimumWidth, calculatedStoneBedMinimumLength ) => {
            availableWidth = availableWidth.ft <= calculatedStoneBedMinimumWidth ? new InchUtility(Math.ceil(calculatedStoneBedMinimumWidth) * 12) : availableWidth;
            availableLength = availableLength.ft <= calculatedStoneBedMinimumLength ? new InchUtility(Math.ceil(calculatedStoneBedMinimumLength) * 12) : availableLength;

            const getPipeBedVolume = ( pipeRows, corrugations ) => {
                const availableWidth = getPipeBedWidth(pipeRows);
                const availableLength = getPipeBedLength(corrugations);
                const availableRowLength = getPipeBedRowLength(corrugations).totalLength;

                const width = new InchUtility(availableWidth.in + ( stonePerimeterOffset.in * 2 ));

                const length = new InchUtility(availableLength.in + ( stonePerimeterOffset.in * 2 ));

                const area = width.ft * length.ft;
                const numberOfBends = pipeRows <= 1 ? 0 : 2;
                const numberOfTees = pipeRows >= 2 ? pipeRows - 2 : 0;

                let pipeBedVolume =
                    pipeNominalDiameter.getPipeRowVolume(pipeRows * availableRowLength.ft) +
                    ( pipeNominalDiameter.getManifoldBendVolume(pipeFitting, numberOfBends) ) +
                    ( pipeNominalDiameter.getManifoldTeeVolume(pipeFitting, numberOfTees) );

                const pipeRowVol = pipeNominalDiameter.getPipeRowVolumeOD(pipeRows * availableRowLength.ft);
                const manifoldBendVol = ( pipeNominalDiameter.getManifoldBendVolume(pipeFitting, numberOfBends, true) );
                const manifoldTeeVol = ( pipeNominalDiameter.getManifoldTeeVolume(pipeFitting, numberOfTees, true) );

                let pipeBedVolumeOD =
                    pipeRowVol +
                    manifoldBendVol +
                    manifoldTeeVol;

                let totalVolume = pipeBedVolume;

                const pipeTotalExcavation = _pipeTotalExcavation(
                    pipeNominalDiameter,
                    stoneAbove,
                    stoneBelow,
                    area);

                const pipeStoneBedAmount = _pipeStoneBedAmount(
                    pipeTotalExcavation,
                    pipeBedVolumeOD);

                const stoneBedVolume = _pipeStoneBedVolume(
                    pipeStoneBedAmount,
                    stonePorosity);

                if (pipeFitting === pipeFittings.getById(PIPE_TYPE_SOIL_TIGHT_PERFORATED_ID)) {
                    totalVolume += stoneBedVolume;
                }

                pipeBedVolume = pipeBedVolumeOD;

                return { totalVolume, stoneBedVolume, pipeBedVolume };
            };
            const getPipeBedWidth = pipeRows => new InchUtility(( pipeRows - 1 ) * pipeRowSpacing.in + pipeNominalDiameter.pipeOD.in);
            const getPipeBedLength = ( corrugations, forceSingle = false ) => new InchUtility(getPipeBedRowLength(corrugations, forceSingle).totalLength.in + getManifoldLongLength(pipeRows).in + getManifoldShortLength(pipeRows).in);
            const getPipeBedRowLength = ( corrugations, forceSingle = false ) => {
                const { pitch, lengthMin, lengthMax, pipeStickToPitchRatio } = pipeNominalDiameter;
                const _availableLength = new InchUtility(corrugations * pitch.in);
                const spigotSpacing = pipeStickToPitchRatio * pitch.in;

                if (_availableLength.in < lengthMin.in) {
                    return {
                        totalLength: lengthMin,
                        rowPipeStickMaxLength: lengthMax,
                        rowPipeStickCutLength: lengthMin,
                        numberOfMaxLengthPipeSticks: 0,
                        numberOfCutLengthPipeSticks: 1,
                    };
                }

                let numberOfMaxLengthPipeSticks = Math.floor(_availableLength.in / lengthMax.in);
                let numberOfCutLengthPipeSticks = 0;
                const getPipeRowLengthLeft = numberOfPipeSticks => {
                    return _availableLength.in - numberOfPipeSticks * lengthMax.in;
                };

                if (getPipeRowLengthLeft(numberOfMaxLengthPipeSticks) === 0) {
                    // If there is no pipe left, there's not reason to cut a pipe
                } else if (forceSingle ||
                    getPipeRowLengthLeft(numberOfMaxLengthPipeSticks) >= lengthMin.in + spigotSpacing ||
                    _availableLength.in / 2 < lengthMin.in) {  // Check if you enough length for a single cut
                    numberOfCutLengthPipeSticks = 1;
                } else {  // Otherwise, must cut two cuts
                    numberOfMaxLengthPipeSticks -= 1;
                    numberOfCutLengthPipeSticks = 2;
                }

                numberOfMaxLengthPipeSticks = numberOfMaxLengthPipeSticks < 0 ? 0 : numberOfMaxLengthPipeSticks;

                let cutLength = getCorrugations(Math.max(getPipeRowLengthLeft(numberOfMaxLengthPipeSticks), lengthMin.in)) * pitch.in;

                if (numberOfCutLengthPipeSticks === 2 || cutLength > lengthMax.in - spigotSpacing) {
                    numberOfCutLengthPipeSticks = 2;

                    const ribs = getCorrugations(
                        Math.max(
                            getPipeRowLengthLeft(numberOfMaxLengthPipeSticks) - spigotSpacing,
                            lengthMin.in));

                    cutLength = ( ribs * pitch.in + spigotSpacing ) / 2;
                }

                const totalInches =
                    ( numberOfMaxLengthPipeSticks * lengthMax.in ) +
                    ( numberOfCutLengthPipeSticks * cutLength );

                return {
                    totalLength: new InchUtility(totalInches),
                    rowPipeStickMaxLength: lengthMax,
                    rowPipeStickCutLength: new InchUtility(cutLength),
                    numberOfMaxLengthPipeSticks,
                    numberOfCutLengthPipeSticks,
                };
            };
            const getPipeRows = availableWidth => Math.floor(availableWidth / pipeNominalDiameter.pipeOD.in);
            const getManifoldLongLength = pipeRows => new InchUtility(pipeRows <= 1 ? 0 : pipeNominalDiameter.pipeOD.in / 2 + pipeLongLegLength.in);
            const getManifoldShortLength = pipeRows => new InchUtility(pipeRows <= 1 ? 0 : pipeNominalDiameter.pipeOD.in / 2 + pipeShortLegLength.in);
            const getCorrugations = availableLength => Math.floor(availableLength / pipeNominalDiameter.pitch.in);

            let width = availableWidth.in;
            let length = availableLength.in;

            // Remove outside perimeter; only use what's available for the pipe bed
            width -= stonePerimeterOffset.in * 2;
            length -= stonePerimeterOffset.in * 2;

            if (width <= 0 || length <= 0) {
                return defaultResult;
            }

            // Calculate the number of pipe rows, pipe sticks, and fittings necessary to design the pipe bed system
            let pipeRows = getPipeRows(width);

            if (pipeRows < 2) {
                pipeRows = 2;
            }

            let rowBedLength = Math.ceil(length - getManifoldShortLength(pipeRows).in - getManifoldLongLength(pipeRows).in);
            let corrugations = getCorrugations(rowBedLength) + 1;

            try {
                const delta = 0.00001;
                while (pipeRows >= 2 && corrugations > 0 && ( getPipeBedWidth(pipeRows).in > width + delta && getPipeBedWidth(pipeRows).in > width - delta )) {
                    --pipeRows;
                }

                while (pipeRows >= 2 && corrugations > 0 && getPipeBedLength(corrugations).in > length) {
                    --corrugations;
                }

                // Won't optimize in all cases
                if (widthOverride === null && lengthOverride === null && requiredVolume !== 0) {
                    while (pipeRows >= 2 && corrugations > 0 && getPipeBedVolume(pipeRows - 1, corrugations).totalVolume > requiredVolume) {
                        --pipeRows;
                    }

                    while (pipeRows >= 2 && corrugations > 0 && getPipeBedVolume(pipeRows, corrugations - 1).totalVolume > requiredVolume) {
                        --corrugations;
                    }
                }
            } catch (exception) {
                console.warn("Unable to build system");
                return defaultResult;
            }

            if (pipeRows < 2) {
                pipeRows = 2;
            }

            let forceSingle = false;
            if (corrugations === 0) {
                corrugations = getCorrugations(pipeNominalDiameter.lengthMin.in);
                forceSingle = true;
            }

            width = getPipeBedWidth(pipeRows);
            length = getPipeBedLength(corrugations, forceSingle);

            let {
                totalLength: rowLength,
                rowPipeStickCutLength,
                numberOfMaxLengthPipeSticks: numberOfMaxLengthPipeSticksPerRow,
                numberOfCutLengthPipeSticks: numberOfCutLengthPipeSticksPerRow,
            } = getPipeBedRowLength(corrugations, forceSingle);

            width = new InchUtility(width.in + ( stonePerimeterOffset.in * 2 ));
            length = new InchUtility(length.in + ( stonePerimeterOffset.in * 2 ));

            const area = width.ft * length.ft;
            const { totalVolume: volume, stoneBedVolume, pipeBedVolume } = getPipeBedVolume(pipeRows, corrugations);
            const pipeSticks = pipeRows * ( numberOfMaxLengthPipeSticksPerRow + numberOfCutLengthPipeSticksPerRow );
            const manifoldTees = ( pipeRows - 2 >= 0 ? pipeRows - 2 : 0 ) * 2;

            // Get the dimensions for the render
            let stoneBedDimensions = {
                x: new InchUtility(0),
                y: new InchUtility(0),
                width: width,
                height: length,
            };
            let pipeBedDimensions = {
                x: new InchUtility(stoneBedDimensions.x.in + stonePerimeterOffset.in),
                y: new InchUtility(stoneBedDimensions.y.in + stonePerimeterOffset.in),
                width: new InchUtility(stoneBedDimensions.width.in - ( stonePerimeterOffset.in * 2 )),
                height: new InchUtility(stoneBedDimensions.height.in - ( stonePerimeterOffset.in * 2 )),
            };
            let centerFittingDimensions = {
                x: pipeBedDimensions.x,
                y: new InchUtility(pipeBedDimensions.y.in + ( pipeNominalDiameter.pipeOD.in / 2 )),
                width: pipeBedDimensions.width,
                height: new InchUtility(pipeBedDimensions.height.in - pipeNominalDiameter.pipeOD.in),
            };
            let rowBedDimensions = {
                x: pipeBedDimensions.x,
                y: new InchUtility(pipeBedDimensions.y.in + ( doesPipeFlowDirectionStartOnTop ? getManifoldShortLength(pipeRows).in : getManifoldLongLength(pipeRows).in )),
                width: pipeBedDimensions.width,
                height: new InchUtility(pipeBedDimensions.height.in - ( ( doesPipeFlowDirectionStartOnBottom ? getManifoldShortLength(pipeRows).in : getManifoldLongLength(pipeRows).in ) * 2 )),
            };

            return {
                dimensions: { width: stoneBedDimensions.width, length: stoneBedDimensions.height },
                stoneBedDimensions,
                pipeBedDimensions,
                centerFittingDimensions,
                rowBedDimensions,
                area,
                volume,
                rowSpacing: pipeRowSpacing.in,
                numberOfPipeRows: pipeRows,
                numberOfPipeSticks: pipeSticks,
                numberOfManifoldTees: manifoldTees,
                rowLength,
                rowPipeStickCutLength,
                numberOfMaxLengthPipeSticksPerRow,
                numberOfCutLengthPipeSticksPerRow,
                stoneBedVolume,
                pipeBedVolume,
            };
        },
    );

    return getDesign(state, { pipeAvailableWidth: widthOverride, pipeAvailableLength: lengthOverride });
};

const {
    doesPipeFlowDirectionStartOnTop: _doesPipeFlowDirectionStartOnTop,
    doesPipeFlowDirectionStartOnBottom: _doesPipeFlowDirectionStartOnBottom,
    doesPipeFlowDirectionEndOnTop: _doesPipeFlowDirectionEndOnTop,
    doesPipeFlowDirectionEndOnBottom: _doesPipeFlowDirectionEndOnBottom,
    doesPipeFlowDirectionStartLeft: _doesPipeFlowDirectionStartLeft,
    doesPipeFlowDirectionStartRight: _doesPipeFlowDirectionStartRight,
    doesPipeFlowDirectionEndLeft: _doesPipeFlowDirectionEndLeft,
    doesPipeFlowDirectionEndRight: _doesPipeFlowDirectionEndRight,
} = pipeSelectors;

export const doesPipeFlowDirectionStartOnTop = () => ( dispatch, getState ) => _doesPipeFlowDirectionStartOnTop(getState());
export const doesPipeFlowDirectionStartOnBottom = () => ( dispatch, getState ) => _doesPipeFlowDirectionStartOnBottom(getState());
export const doesPipeFlowDirectionEndOnTop = () => ( dispatch, getState ) => _doesPipeFlowDirectionEndOnTop(getState());
export const doesPipeFlowDirectionEndOnBottom = () => ( dispatch, getState ) => _doesPipeFlowDirectionEndOnBottom(getState());
export const doesPipeFlowDirectionStartLeft = () => ( dispatch, getState ) => _doesPipeFlowDirectionStartLeft(getState());
export const doesPipeFlowDirectionStartRight = () => ( dispatch, getState ) => _doesPipeFlowDirectionStartRight(getState());
export const doesPipeFlowDirectionEndLeft = () => ( dispatch, getState ) => _doesPipeFlowDirectionEndLeft(getState());
export const doesPipeFlowDirectionEndRight = () => ( dispatch, getState ) => _doesPipeFlowDirectionEndRight(getState());



// WEBPACK FOOTER //
// ./src/actions/pipe-bed-actions.js